/**
 * beautifi - Core Type Definitions
 *
 * This file contains all TypeScript interfaces and types used throughout
 * the beautifi plugin.
 */

/**
 * Animation intensity levels
 */
export type AnimationIntensity = 'subtle' | 'medium' | 'strong';

/**
 * Animation type styles
 */
export type AnimationType = 'breathing' | 'parallax' | 'sway' | 'auto';

/**
 * Animation states
 */
export type AnimationState = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

/**
 * Error codes for different failure scenarios
 */
export type LivePhotoErrorCode =
    | 'API_ERROR'
    | 'NETWORK_ERROR'
    | 'TIMEOUT_ERROR'
    | 'VALIDATION_ERROR'
    | 'RATE_LIMIT_ERROR'
    | 'RENDER_ERROR'
    | 'UNKNOWN_ERROR';

/**
 * Event types emitted by the plugin
 */
export type LivePhotoEventType =
    | 'init'
    | 'imageDetected'
    | 'animationStart'
    | 'animationComplete'
    | 'animationPause'
    | 'animationResume'
    | 'animationError'
    | 'videoStart'
    | 'videoReady'
    | 'videoError'
    | 'destroy';

/**
 * Animation mode - determines how images are animated
 * - 'auto': CSS animation immediately + AI video in background (default)
 * - 'css': CSS animation only, no video generation
 * - 'video': Wait for video, no CSS animation
 */
export type AnimationMode = 'auto' | 'css' | 'video';

/**
 * Image source type - how the image URL was obtained
 * - 'img': From an <img> element's src attribute
 * - 'background-image': From an element's CSS background-image property
 */
export type ImageSourceType = 'img' | 'background-image';

/**
 * Video generation state
 */
export type VideoState = 'idle' | 'generating' | 'ready' | 'error';

/**
 * Global plugin configuration options
 */
export interface LivePhotoOptions {
    /** API key for Gemini backend (required for animation generation) */
    apiKey: string;
    /** Cloud Functions endpoint URL (optional, uses default if not provided) */
    endpoint?: string;
    /** Settings API endpoint URL for portal integration (optional) */
    settingsEndpoint?: string;
    /** Settings cache TTL in milliseconds (default: 5 minutes) */
    settingsCacheTTL?: number;
    /** CSS selector for target images (default: 'img') */
    selector?: string;
    /** Default animation intensity (default: 'subtle') */
    intensity?: AnimationIntensity;
    /** Default animation type (default: 'auto') */
    type?: AnimationType;
    /** Whether animations should loop (default: true) */
    loop?: boolean;
    /** Respect prefers-reduced-motion (default: true) */
    respectReducedMotion?: boolean;
    /** Enable debug logging (default: false) */
    debug?: boolean;
    /** Viewport intersection threshold for lazy loading (default: 0.1) */
    threshold?: number;
    /** Root margin for eager loading (default: '50px') */
    rootMargin?: string;
    /** Request timeout in milliseconds (default: 10000) */
    timeout?: number;
    /** Maximum retry attempts (default: 3) */
    maxRetries?: number;
    /** Animation mode: 'auto' | 'css' | 'video' (default: 'auto') */
    mode?: AnimationMode;
    /** Video endpoint URL (if different from main endpoint) */
    videoEndpoint?: string;
    /** Maximum concurrent video generations/playbacks (default: 3) */
    maxConcurrentVideos?: number;
    /** Margin ahead of viewport to start video loading (default: '200px') */
    loadAheadMargin?: string;
    /** Margin beyond which videos are unloaded (default: '500px') */
    unloadMargin?: string;
    /**
     * Opt-in mode - when true, only animate images with data-beautifi or data-beautifi-animate
     * When false (default), animate all images matching the selector
     */
    optIn?: boolean;
}

/**
 * Per-image animation configuration (from data attributes)
 */
export interface AnimationConfig {
    /** Whether animation is enabled for this image */
    enabled: boolean;
    /** Animation mode override from data attributes or portal settings */
    mode?: AnimationMode;
    /** Animation intensity level */
    intensity: AnimationIntensity;
    /** Animation type/style */
    type: AnimationType;
    /** Whether animation loops continuously */
    loop: boolean;
    /** Delay before animation starts (ms) */
    delay: number;
    /** Existing Veo operation started server-side (optional) */
    videoOperationId?: string;
    /** Current server-side video generation status (optional) */
    videoStatus?: 'pending' | 'processing' | 'completed' | 'error' | 'retry';
    /** Pre-generated video URL when already available (optional) */
    videoUrl?: string;
}

/**
 * Animation frame data from Gemini API
 */
export interface AnimationFrameData {
    /** Frame timestamp */
    timestamp: number;
    /** Transform data for this frame */
    transform: {
        translateX: number;
        translateY: number;
        scale: number;
        rotate: number;
    };
    /** Optional opacity value */
    opacity?: number;
}

/**
 * Gemini API response for animation generation
 */
export interface GeminiAnimationResponse {
    /** Whether the request was successful */
    success: boolean;
    /** Unique animation ID */
    animationId: string;
    /** Animation duration in milliseconds */
    duration: number;
    /** Frame rate (fps) */
    frameRate: number;
    /** Animation keyframes */
    frames: AnimationFrameData[];
    /** Detected animation type */
    detectedType: AnimationType;
    /** Cache status (hit/miss) */
    cacheStatus?: 'hit' | 'miss';
    /** Error message if success is false */
    error?: string;
}

/**
 * API request payload
 */
export interface GeminiAnimationRequest {
    /** Image URL or hash */
    imageUrl: string;
    /** Image hash for caching */
    imageHash?: string;
    /** Requested animation type */
    type: AnimationType;
    /** Requested intensity */
    intensity: AnimationIntensity;
    /** Whether to loop */
    loop: boolean;
}

/**
 * Tracked image element with metadata
 */
export interface TrackedImage {
    /** Original element (img or element with background-image) */
    element: HTMLElement;
    /** Unique ID for this tracked image */
    id: string;
    /** Current animation state */
    state: AnimationState;
    /** Animation configuration for this image */
    config: AnimationConfig;
    /** How the image URL was obtained */
    sourceType: ImageSourceType;
    /** Extracted image URL (from src or background-image) */
    imageUrl: string;
    /** API response data (when loaded) */
    animationData?: GeminiAnimationResponse;
    /** Error if animation failed */
    error?: LivePhotoError;
}

/**
 * Base event payload
 */
export interface LivePhotoEventBase {
    /** Event type */
    type: LivePhotoEventType;
    /** Event timestamp */
    timestamp: number;
}

/**
 * Image-related event payload
 */
export interface LivePhotoImageEvent extends LivePhotoEventBase {
    /** Image element involved */
    element: HTMLElement;
    /** Image tracking ID */
    imageId: string;
}

/**
 * Error event payload
 */
export interface LivePhotoErrorEvent extends LivePhotoImageEvent {
    type: 'animationError';
    /** Error details */
    error: LivePhotoError;
}

/**
 * Event handler function type
 */
export type LivePhotoEventHandler<T extends LivePhotoEventBase = LivePhotoEventBase> = (
    event: T
) => void;

/**
 * Viewport observer callback
 */
export type ViewportCallback = (image: TrackedImage, isIntersecting: boolean) => void;

/**
 * Custom error class for beautifi
 */
export class LivePhotoError extends Error {
    constructor(
        message: string,
        public readonly code: LivePhotoErrorCode,
        public readonly originalError?: Error
    ) {
        super(message);
        this.name = 'LivePhotoError';
        // Ensure prototype chain is set correctly
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, LivePhotoError);
        }
    }
}

/**
 * Default configuration values
 */
export const DEFAULT_OPTIONS: Required<Omit<LivePhotoOptions, 'apiKey'>> = {
    endpoint: 'https://api.beautifi.uk/animate',
    settingsEndpoint: 'https://settings-api-oy7ayfiglq-uc.a.run.app/query',
    settingsCacheTTL: 5 * 60 * 1000, // 5 minutes
    selector: 'img, [data-beautifi], [data-beautifai]',
    intensity: 'subtle',
    type: 'auto',
    loop: true,
    respectReducedMotion: true,
    debug: false,
    threshold: 0.1,
    rootMargin: '50px',
    timeout: 10000,
    maxRetries: 3,
    mode: 'auto',
    videoEndpoint: 'https://api.beautifi.uk',
    maxConcurrentVideos: 3,
    loadAheadMargin: '200px',
    unloadMargin: '500px',
    optIn: false,
};

/**
 * Default animation configuration
 */
export const DEFAULT_ANIMATION_CONFIG: AnimationConfig = {
    enabled: false,
    intensity: 'subtle',
    type: 'auto',
    loop: true,
    delay: 0,
};
